import {
  Box,
  IconButton,
  Menu,
  Tooltip,
  Typography,
  MenuItem,
  Dialog
} from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/redux/hooks';
import CustomDataGrid from '../../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import {
  capitalizeFirstLetter,
  convertDatetoEpoch,
  convertEpochToDateString,
  convertTo12HourFormat,
  debounce
} from '../../../../../../utils/commonFunctions';
import { Icon } from '@iconify/react';
import constant from '../../../../../../utils/constants';
import CustomBreadcrumbs from '../../../../../../common/components/custombreadcrumbs/CustomBreadcrumbs';
import CustomButton from '../../../../../../common/components/buttons/CustomButton';
import AddTrip from './AddTrip';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CustomTextField from '../../../../../../common/components/customized/customtextfield/CustomTextField';
import { useForm } from 'react-hook-form';
import {
  autoPlannerBookingAction,
  autoPlannerDeleteAction,
  autoPlannerTripsAction
} from '../../../../redux/reducer/autoPlannerSlices/autoplanner';
import { GridActionsCellItem } from '@mui/x-data-grid';
import ConfirmationPopup from '../../../../../../common/components/confirmationpopup/ConfirmationPopup';

interface Props {
  payloads: any;
  agentName: any;
  setBreadCrumbs: any;
  pageSizes: number;
  setExpanded: any;
  clearFilter: any;
  setPageSizes: any;
  filterPayload: any;
}

interface Row {
  id: number;
  agentname: string;
  guestname: string;
  fromlocation: string;
  tolocation: string;
  startDateTime: string;
  adultcount: number;
  childcount: number;
  referenceid: number;
  tripType: string;
  source: string;
  startLat: number;
  startLng: number;
  target: string;
  passengerJourneys: any[];
  targetLat: number;
  targetLng: number;
  groupId: string;
  startTimestamp?: number;
}

interface Column {
  field: string;
  headerName: string;
  minWidth: number;
  flex: number;
  renderCell?: (params: { row: Row }) => any;
  textAlign?: string;
}

interface PaginationModel {
  page: number;
  pageSize: number;
}

const BookingSic = ({
  payloads,
  agentName,
  setPageSizes,
  pageSizes,
  setBreadCrumbs,
  setExpanded,
  clearFilter,
  filterPayload
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const BookingData = useAppSelector(state => state.AutoPlannerBooking);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [view, setView] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDialog, setIsDialog] = useState('');
  const [pageSize, setPageSize] = useState(pageSizes || 20);
  const [rows, setRows] = useState([]);
  const [tripsRows, setTripsRows] = useState<Row[]>([]);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [IspageSize, setIsPageSize] = useState(20);
  const [IspageNo, setIsPageNo] = useState(1);
  const [tripPayload, setTripPayload] = useState<any>({ pageNo, pageSize });
  const [rowCount, setRowCount] = useState(0);
  const [IsrowCount, setIsRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<any>([]);
  const [tripsGroupRows, setGroupTripsRows] = useState<Row[]>([]);
  const [bookingsPayload, setBookingsPayload] = useState<any>(null);
  const [filter, setFilter] = useState('');
  const openMenu = Boolean(anchorEl);

  const tripsData = useAppSelector(state => state.AutoPlannerTrip);
  const { data } = useAppSelector(state => state.auth);
  const Routes = useAppSelector(state => state.autoPlannerRoutes.data || []);
  const Agents = useAppSelector(state => state.autoPlannerAgent.data || []);
  const { isLoading } = useAppSelector(state => state.AutoPlannerDeleteTrip);

  const dispatch = useAppDispatch();

  const { control } = useForm();

  const handleViewGroupTrip = (event: React.MouseEvent<HTMLElement>, params: any) => {
    if (params !== null) {
      setSelectedGroup(params);
      const payload = {
        pageNo: 1,
        pageSize: 20,
        tourName: params.tourName,
        mode: 'SIC',
        data: params.date,
        agentName: agentName
      };
      setBookingsPayload(payload);
      dispatch(autoPlannerBookingAction(payload));
      setView(true);
    }
  };

  const agentNames =
    Agents?.map((value: string) => ({
      id: value,
      label: capitalizeFirstLetter(value)
    })) || [];

  const configuredRoute =
    Routes?.map((value: string) => ({
      id: value,
      label: value
    })) || [];

  const handleSortModelChange = (model: any) => {
    setSortModel(model);

    const payload = {
      ...tripPayload,
      sortBy: model[0]?.sort,
      sortByField: model[0]?.field
    };
    setTripPayload(payload);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewTrip = () => {
    handleMenuClose();
    setViewOpen(true);
  };

  const handleUpdateTrip = () => {
    handleMenuClose();
    setIsOpen(true);
  };

  const handleDeleteTrip = async () => {
    if (selectedRow) {
      const payload = {
        selectedRow: selectedRow,
        tripMode: 'SIC',
        pageDetails: { pageNo, pageSize }
      };
      await dispatch(autoPlannerDeleteAction(payload));
      setIsDialog('');
    }
  };

  const handleMenuClick = (row: Row) => {
    if (selectedGroup !== null) {
      const value = {
        source: row.source,
        startLat: row.startLat,
        startLng: row.startLng,
        // startTimestamp: row.startTimestamp,
        destination: selectedGroup.destination,
        endLat: selectedGroup.endLat,
        endLng: selectedGroup.endLng,
        endTimestamp: selectedGroup.endTimestamp,
        tripTotalDuration: selectedGroup.tripTotalDuration,
        mode: selectedGroup.mode
      };
      setSelectedRow({ ...row, ...value, ...selectedGroup });
    }
  };

  const handleBookingsPaginationModelChange = (newPaginationModel: PaginationModel) => {
    const { pageSize: newPageSize, page: newPageNo } = newPaginationModel;
    setIsPageSize(newPageSize);
    setIsPageNo(newPageNo + 1);
    const newPage = newPageNo + 1;
    const payload = {
      ...bookingsPayload,
      pageNo: newPage,
      pageSize: newPageSize
    };
    dispatch(autoPlannerBookingAction(payload));
  };

  const handlePaginationModelChange = (newPaginationModel: PaginationModel) => {
    const { pageSize: newPageSize, page: newPageNo } = newPaginationModel;
    setPageSize(newPageSize);
    setPageNo(newPageNo + 1);
    const newPage = newPageNo + 1;
    const payload = {
      ...tripPayload,
      pageNo: newPage,
      pageSize: newPageSize
    };
    setTripPayload(payload);
  };

  const groupColumns: Column[] = [
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 150,
      flex: 1,
      getActions: (params: any) => {},
      renderCell: (params: any) => (
        <Box
          onClick={event => {
            event.stopPropagation();
            handleViewGroupTrip(event, params.row);
          }}
        >
          <Tooltip title='View group bookings' arrow>
            <Typography className='view-routes'>View Bookings</Typography>
          </Tooltip>
        </Box>
      )
    },

    {
      field: 'tourName',
      headerName: 'Tour Name',
      minWidth: 230,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },

    {
      field: 'date',
      headerName: 'Trip Date',
      minWidth: 120,
      flex: 1
    },

    {
      field: 'pickupWindow',
      headerName: 'Pickup Time Window',
      minWidth: 180,
      flex: 1
    },
    {
      field: 'returnTime',
      headerName: 'Return Time',
      minWidth: 120,
      flex: 1
    },
    {
      field: 'mode',
      headerName: 'Tour Mode',
      minWidth: 120,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.row.mode} arrow>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {params.row.mode}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'totalAdultCount',
      headerName: 'Adult Count',
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.row.totalAdultCount} arrow>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {params.row.totalAdultCount}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'totalChildCount',
      headerName: 'Child Count',
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.row.totalChildCount} arrow>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {params.row.totalChildCount}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'netCount',
      headerName: 'Total Passenger',
      minWidth: 130,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.row.netCount} arrow>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {params.row.netCount}
          </Box>
        </Tooltip>
      )
    }
  ]?.map((item: any) => ({
    ...item,
    sortable: false,
    align: ['actions']?.includes(item?.field) ? 'center' : 'left',
    headerAlign: ['actions']?.includes(item?.field) ? 'center' : 'left'
  }));

  const columns: Column[] = [
    {
      field: 'actions',
      headerName: 'Action',
      type: 'actions',
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      getActions: (params: any) => {
        const actions = [
          <GridActionsCellItem
            key='edit'
            icon={<Icon icon='ic:baseline-edit' className='menu-icon update' />}
            label={constant.Update}
            onClick={() => {
              handleMenuClick(params?.row);
              handleUpdateTrip();
            }}
            showInMenu
          />
        ];

        // Conditionally add Delete action
        if (data?.role === 'ROLE_OPERATOR' || data?.role === 'ROLE_AUTOPLANNER_ADMIN') {
          actions?.push(
            <GridActionsCellItem
              key='delete'
              icon={
                <Icon
                  icon='ic:outline-delete'
                  className='menu-icon'
                  style={{ color: '#FF4343' }}
                />
              }
              label={constant.Delete}
              onClick={() => {
                handleMenuClick(params.row);
                setIsDialog('Delete');
                handleMenuClose();
              }}
              showInMenu
            />
          );
        }

        return actions;
      }
    },

    // renderCell: (params: any) => (
    //   <Box className='actions'>
    //     <Tooltip title='Menu' placement='right' arrow>
    //       <IconButton
    //         id='basic-button'
    //         aria-controls={isDialog ? 'basic-menu' : undefined}
    //         aria-haspopup='true'
    //         aria-expanded={isDialog ? 'true' : undefined}
    //         onClick={event => {
    //           event.stopPropagation();
    //           setIsDialog('menu');
    //           handleMenuClick(event, params.row);
    //         }}
    //       >
    //         <Icon icon='eva:menu-2-outline' className='menu-bg' />
    //       </IconButton>
    //     </Tooltip>
    //     <Menu
    //       id='basic-menu'
    //       anchorEl={anchorEl}
    //       open={openMenu}
    //       onClose={handleMenuClose}
    //       MenuListProps={{
    //         'aria-labelledby': 'basic-button'
    //       }}
    //     >
    //       {/* <MenuItem className='menu-dialog' onClick={handleViewTrip}>
    //         <Icon icon='bx:trip' className='menu-icon view' />
    //         {constant.View}
    //       </MenuItem> */}
    //       <MenuItem className='menu-dialog' onClick={handleUpdateTrip}>
    //         <Icon icon='ic:baseline-edit' className='menu-icon update' />
    //         {constant.Update}
    //       </MenuItem>
    //       {data?.role === 'ROLE_OPERATOR_ADMIN' && (
    //         <MenuItem
    //           className='menu-dialog'
    //           onClick={() => {
    //             setIsDialog('Delete');
    //             handleMenuClose();
    //           }}
    //         >
    //           <Icon
    //             icon='ic:outline-delete'
    //             className='menu-icon'
    //             style={{ color: '#FF4343' }}
    //           />
    //           {constant.Delete}
    //         </MenuItem>
    //       )}
    //     </Menu>
    //   </Box>
    // )

    { field: 'agent_name', headerName: 'Agent Name', minWidth: 200, flex: 1 },
    {
      field: 'guest_name',
      headerName: 'Guest Name',
      minWidth: 200,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'guestContactNumber',
      headerName: 'Guest Contact Number',
      minWidth: 200,
      flex: 1,
      valueGetter: (params: any) => (params?.value ? params.value : '-')
    },
    { field: 'adult_count', headerName: 'Adult Count', minWidth: 130, flex: 1 },
    { field: 'child_count', headerName: 'Child Count', minWidth: 130, flex: 1 },
    {
      field: 'reference',
      headerName: 'Reference ID',
      minWidth: 120,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'transferPickup',
      headerName: 'Pickup Location',
      minWidth: 250,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    }
  ]?.map((item: any) => ({ ...item, sortable: false }));

  const handleFilterChange = useCallback(
    debounce((event: string) => {
      setFilter(event);
      if (rows) {
        const payloadSIC = {
          ...tripPayload,
          tripMode: 'SIC',
          search: event
        };
        setTripPayload(payloadSIC);
        dispatch(autoPlannerTripsAction(payloadSIC));
      }
    }),
    [tripPayload]
  );

  useEffect(() => {
    if (
      BookingData?.data?.data !== null &&
      BookingData?.data?.data?.passengerJourneyInfoList
    ) {
      const data = BookingData?.data?.data?.passengerJourneyInfoList?.map(
        (item: any, index: number) => ({
          ...item,
          sno: index + 1,
          id: item.trip_id,
          transferLocation: item.transfer_info?.pickupCoordinates,
          transferTripInfo: item.transfer_info ? 'Yes' : 'No',
          transferPickup: item.source ? item.source : '-',
          reference: item.ref_number ? item.ref_number : '-',
          pickupTimestamp: item.transfer_info
            ? convertEpochToDateString(item.transfer_info.pickupTimestamp)
            : '-'
        })
      );
      setTripsRows(data);
      setIsRowCount(BookingData?.data?.data.count);
    } else {
      setTripsRows([]);
    }
  }, [BookingData]);

  useEffect(() => {
    if (
      tripsData?.data?.data !== null &&
      tripsData?.data?.data?.groupedTrips?.length > 0
    ) {
      setRows(tripsData?.data?.data?.groupedTrips);
      setRowCount(tripsData?.data?.data?.count || 0);
    } else {
      setRows([]);
    }
  }, [tripsData?.data]);

  useLayoutEffect(() => {
    if (rows?.length > 0) {
      const data = rows?.map((item: any, index: number) => {
        const [startTime, endTime] =
          typeof item?.pickupWindow === 'string' && item.pickupWindow?.includes('-')
            ? item?.pickupWindow.split('-')
            : ['', ''];

        return {
          ...item,
          sno: index + 1,
          id: index + 1,
          pickupWindow: startTime
            ? `${convertTo12HourFormat(startTime)} - ${convertTo12HourFormat(endTime)}`
            : '-',
          returnTime: item?.returnTime ? convertTo12HourFormat(item?.returnTime) : '-'
        };
      });

      setGroupTripsRows(data);
    } else {
      setGroupTripsRows([]);
    }
  }, [rows]);

  const payloadSIC = {
    ...payloads,
    ...tripPayload,
    tripMode: 'SIC'
  };

  // useEffect(() => {
  //   if (payload) {
  //     const payloadSIC = {
  //       ...payload,
  //       tripMode: 'SIC'
  //     };
  //     setTripPayload({ ...payload, tripMode: 'SIC' });
  //     dispatch(autoPlannerTripsAction(payloadSIC));
  //   }
  // }, [payload]);

  const handleBookings = async (tripPayload: any) => {
    await dispatch(autoPlannerTripsAction(tripPayload));
  };

  useEffect(() => {
    if (tripPayload) {
      const payloadSIC = {
        ...tripPayload,
        tripMode: 'SIC'
      };

      if (tripPayload?.autoplannerID) {
        handleBookings(payloadSIC);
      }
    }
  }, [tripPayload]);

  useEffect(() => {
    setBreadCrumbs({
      setViews: setView,
      views: view,
      setPageNo: setPageNo,
      setPageSize: setPageSize
    });
  }, [view, setPageNo, setPageSize]);

  useEffect(() => {
    const payload = {
      ...tripPayload,
      ...payloads
    };
    setTripPayload(payload);
  }, [payloads]);

  useEffect(() => {
    if (pageSize) setPageSizes(pageSize);
  }, [pageSize]);

  return (
    <Box>
      <Box className='trip-datas'>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <CustomBreadcrumbs
            className='tracking-heading'
            itemOne={'Tour'}
            itemTwo={'Bookings'}
            itemTwoState={view}
            setView={setView}
            handleItemOneClick={() => {
              if (view) dispatch(autoPlannerTripsAction(payloadSIC));
            }}
          />
          {!view && (
            <Box sx={{ width: 200 }}>
              <CustomTextField
                name='search'
                control={control}
                id='filter-input'
                placeholder='Search...'
                value={filter}
                variant='standard'
                icon={<SearchOutlinedIcon color='primary' />}
                onChangeCallback={(e: any) => handleFilterChange(e)}
              />
            </Box>
          )}
        </Box>
        {!view ? (
          <CustomDataGrid
            rows={tripsGroupRows}
            sortingMode='server'
            columns={groupColumns}
            onRowClick={(event: any, row) => {
              handleViewGroupTrip(event, row);
              setSelectedGroup(row);
            }}
            loading={tripsData?.isLoading}
            onSortModelChange={handleSortModelChange}
            rowCount={rowCount}
            type='logistics'
            onPaginationModelChange={handlePaginationModelChange}
            pageNo={pageNo}
            pageSize={pageSize}
            paginationModel={{
              page: pageNo ? pageNo - 1 : 0,
              pageSize: pageSize ? pageSize : 20
            }}
          />
        ) : (
          <CustomDataGrid
            rows={tripsRows}
            rowCount={IsrowCount}
            type='autoplanner'
            columns={columns}
            onRowClick={(event: any, row) => {
              handleUpdateTrip();
              if (selectedGroup !== null) {
                const value = {
                  source: row.source,
                  startLat: row.startLat,
                  startLng: row.startLng,
                  // startTimestamp: row.startTimestamp,
                  destination: selectedGroup.destination,
                  endLat: selectedGroup.endLat,
                  endLng: selectedGroup.endLng,
                  endTimestamp: selectedGroup.endTimestamp,
                  tripTotalDuration: selectedGroup.tripTotalDuration,
                  mode: selectedGroup.mode
                };
                setSelectedRow({ ...row, ...value, ...selectedGroup });
              }
            }}
            pageNo={IspageNo}
            pageSize={IspageSize}
            loading={BookingData?.isLoading}
            onPaginationModelChange={handleBookingsPaginationModelChange}
          />
        )}
      </Box>

      <Dialog open={isDialog === 'Delete'}>
        <Box
          sx={{
            textAlign: 'center',
            padding: '5%',
            minWidth: '350px'
          }}
        >
          <Typography>{constant.DeleteTrip}</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingTop: '5%',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ marginRight: '12px' }}>
              <CustomButton
                className='saveChanges'
                category='Yes'
                loading={isLoading}
                onClick={() => {
                  handleDeleteTrip();
                }}
              />
            </Box>
            <CustomButton
              className='cancel'
              category='No'
              onClick={() => setIsDialog('')}
            />
          </Box>
        </Box>
      </Dialog>
      {/* {isDialog === 'Delete' && (
        <ConfirmationPopup
          open={isDialog === 'Delete'}
          loading={isLoading}
          onClose={() => setIsDialog('')}
          onConfirm={() => {
            handleDeleteTrip();
          }}
          title='Delete Booking'
          messages={[
            'This booking includes scheduled entries.',
            'Deleting it will also remove all associated schedules.'
          ]}
          confirmText='Yes, Delete'
          cancelText='No'
        />
      )} */}

      {isOpen && (
        <AddTrip
          isOpen={isOpen}
          filterPayload={filterPayload}
          setIsOpen={setIsOpen}
          selectedRow={selectedRow}
          pageDetails={tripPayload}
          selectedGroup={selectedGroup}
          // setTripMode={handleTrip}
          Agents={agentNames}
          Routes={configuredRoute}
        />
      )}
    </Box>
  );
};

export default BookingSic;
